import React, { useEffect, useState } from "react";
import {
  Breadcrumbs,
  Container,
  Grid,
  Link,
  Typography,
  Box,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import { format, parse } from "date-fns";
import { useAuthUser, useSignOut } from "react-auth-kit";
import {
  isDealerSite,
  requestApi,
  showAlertAutoDis,
  validateForm,
} from "../../utils/utils";
import GuestBar from "../../components/GuestInfo/GuestBar";
import Payment from "../../components/Booking/Payment/Payment";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "../../components/Booking/Payment/CheckoutForm";
import CheckoutFormFlywire from "../../components/Booking/Payment/CheckoutFormFlywire";
import { loadStripe } from "@stripe/stripe-js/pure";
import Icon from "@mui/material/Icon";
import * as yup from "yup";
// import WarningAmberIcon from '@mui/icons-material/WarningAmber';

let billingDetailsValidation = yup.object().shape({
  country: yup.string().required("The country is required"),
  state: yup.string().required("The state is required"),
  address_city: yup.string().required("The city is required"),
  address_line1: yup.string().required("The address is required"),
  address_zip: yup.string().required("The Zip code is required"),
  name: yup.string().required("The Name is required"),
  email: yup.string().required("The Email is required"),
});

const DashboardPaymentPage = () => {
  const [booking, setBooking] = useState(null);
  const {stripeKey} = window;
  const [payments, setPayments] = useState([]);
  const [stripePromise, setStripePromise] = useState(false);
  const [payThisMuch, setPayThisMuch] = useState(0.0);
  const [billingDetails, setBillingDetails] = useState({
    country: "",
    state: "",
    address_city: "",
    address_line1: "",
    address_line2: "",
    address_zip: "",
    email: "",
    name: "",
  });
  const [allowPay, setAllowPay] = useState(false);
  const [message, setMessage] = useState("");
  const [expanded, setExpanded] = useState("panel1");
  const { id } = useParams();
  const signOut = useSignOut();
  const history = useHistory();
  const user = useAuthUser();
  const [statusFlywire, setStatusFlywire] = useState(null);
  const [statusStripe, setStatusStripe] = useState(null);

  useEffect(() => {
    getStatus();
    getBookingById().catch();
    setStripePromise(loadStripe(stripeKey));
  }, []);

  const getStatus = async () => {
    try {
      const { data: flywireData } = await requestApi(
        "get-flywire-status",
        "GET"
      );
      if (flywireData.status === "success") setStatusFlywire(flywireData.data);
      const { data: stripeData } = await requestApi("get-stripe-status", "GET");
      if (stripeData.status === "success") setStatusStripe(stripeData.data);
    } catch (e) {
      showAlertAutoDis(
        {
          title: "Error",
          text: "Unable to complete the request, please try again later.",
        },
        () => {
          history.push(`/booking/my-bookings/${id}`);
        }
      );
    }
  };

  useEffect(() => {
    if (booking) {
      checkTerms();
      checkToPay().catch();
    }
  }, [booking?.id]);

  const checkTerms = () => {
    if (booking && parseInt(booking.termsAccepted) === 0)
      return history.push(`/booking/my-bookings/${booking.id}/terms`);
  };
  const checkToPay = async () => {
    const url = `/booking/check-to-pay/${booking.id}`;
    const { data } = await requestApi(url, "POST");

    if (data.status === "success") {
      const allow = !!data?.allowed_payment;
      setAllowPay(allow);
      if (!allow && data.message) setMessage(data.message);
    }
  };
  const getBookingById = async () => {
    try {
      const { data } = await requestApi(`/booking/get/${id}`);
      if (data.status === "failed") return history.push("/");
      setBooking(data.booking);
      setEmailAndName();
      setPayments(data.paySchedule.payschedule);
    } catch (e) {
      if (e.status === 401) signOut();
    }
  };

  const setEmailAndName = () => {
    const name = user().firstName + " " + user().lastName || "";
    const email = user().email || "";
    setBillingDetails({ ...billingDetails, email, name });
  };

  const setDetails = (event) => {
    setBillingDetails({
      ...billingDetails,
      [event.target.name]: event.target.value,
    });
  };

  const sendStripeToken = async (token) => {
    if (
      !(await validateForm(
        billingDetailsValidation,
        billingDetails,
        "Billing Details Form"
      ))
    )
      return;
    const paymentData = {
      amount: payThisMuch,
      billingDetails,
    };
    const {
      name,
      brand,
      address_line1,
      address_city,
      address_zip,
      cvc_check,
      address_zip_check,
      country,
      last4,
    } = token.card;
    paymentData.cardInfo = {
      name,
      brand,
      address_line1,
      address_city,
      address_zip,
      cvc_check,
      address_zip_check,
      country,
      last4,
    };
    paymentData.token = token.id;
    paymentData.created = token.created;
    await requestApi(
      `/booking/payment/${id}`,
      "POST",
      paymentData,
      ({ data }) => {
        let successPayment = false;
        if ("status" in data && data["status"] === "success") {
          successPayment = !!data?.successPayment;
          if (successPayment)
            showAlertAutoDis(
              { title: "Success", text: "Payment successful" },
              () => {
                history.push(`/booking/my-bookings/${id}`);
              }
            );
        } else if ("message" in data)
          showAlertAutoDis({ title: "Error", text: data["message"] });
      }
    );
  };

  const handleChangeAccordeon = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const sendFlywireToken = async (token) => {
    const paymentData = {
      amount: payThisMuch,
      billingDetails: {
        name: billingDetails["name"],
        email: billingDetails["email"],
      },
    };
    const {
      amount,
      amountCurrency,
      digits,
      expirationMonth,
      expirationYear,
      payerAmount,
      payerAmountCurrency,
      paymentMethod,
      reference,
      sig,
      status,
    } = token;
    paymentData.cardInfo = {
      amount,
      amountCurrency,
      digits,
      expirationMonth,
      expirationYear,
      payerAmount,
      payerAmountCurrency,
      paymentMethod,
      reference,
      sig,
      status,
    };
    paymentData.token = token.token;

    await requestApi(
      `paymentFlywire/${id}`,
      "POST",
      paymentData,
      ({ data }) => {
        let successPayment = false;
        if ("status" in data && data["status"] === "success") {
          successPayment = !!data["successPayment"];
          if (successPayment)
            showAlertAutoDis(
              { title: "Success", text: "Payment successful" },
              () => {
                history.push(`/booking/my-bookings/${id}`);
              }
            );
        } else if ("message" in data)
          showAlertAutoDis({ title: "Error", text: data["message"] });
      }
    );
  };

  return (
    <>
      <GuestBar>
        {booking && (
          <Breadcrumbs aria-label="breadcrumb" separator={">"}>
            <Link
              component={RouterLink}
              underline="hover"
              color="inherit"
              to="/booking/my-bookings"
            >
              Bookings
            </Link>
            <Link
              component={RouterLink}
              underline="hover"
              color="inherit"
              to={`/booking/my-bookings/${booking.id}`}
            >
              {format(
                parse(booking.tripDate, "yyyy-MM-dd", new Date()),
                "LLLL d, yyyy"
              )}
            </Link>
            <Typography color="text.primary">
              Payment for booking {booking.id}
            </Typography>
          </Breadcrumbs>
        )}
      </GuestBar>

      {allowPay && <Container sx={{ marginTop: '20px' }}>
                <Typography variant="h3" component="h3" align="center" sx={{ marginBottom: '20px' }}>
                    Make a Secure Payment Now
                </Typography>
                <Box sx={{ backgroundColor: '#E7EBF0', padding: '20px', borderRadius: '10px' }}>
                    {statusStripe === '1' &&
                        <Accordion expanded={expanded === 'panel1'} onChange={handleChangeAccordeon('panel1')} sx={{ backgroundColor: '#FFF', marginBottom: '20px' }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ fontSize: '26px !important' }} />}
                                aria-controls="panel1bh-content"
                                id="panel1bh-header"
                            >
                                <Typography variant="h6" component="h6" sx={{ fontSize: '20px', fontWeight: '400' }}>
                                    USD Payment
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Payment paySchedule={payments} payThisMuch={payThisMuch}
                                    setPayThisMuch={(amount) => setPayThisMuch(parseFloat(amount))}
                                    allowCustomPay={parseInt(booking['Type']) === 2} />

                                {payThisMuch > 0 && <div style={{ marginTop: 30 }}>
                                    <Typography align={'center'} id="transition-modal-title" variant={'h3'}>
                                        Pay Now ${payThisMuch.formatMoney()} USD
                                    </Typography>
                                    {stripePromise && <Elements stripe={stripePromise}>
                                        <CheckoutForm
                                            setBilling={setDetails}
                                            billingDetails={billingDetails}
                                            sendStripeToken={sendStripeToken} />
                                    </Elements>}
                                </div>}
                            </AccordionDetails>
                        </Accordion>
                    }
                    {statusFlywire === '1' &&
                        <Accordion expanded={expanded === 'panel2'} onChange={handleChangeAccordeon('panel2')} sx={{ backgroundColor: '#FFF' }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ fontSize: '26px !important' }} />}
                                aria-controls="panel2bh-content"
                                id="panel2bh-header"
                            >
                                <Typography variant="h6" component="h6" sx={{ fontSize: '20px', fontWeight: '400' }}>
                                    Local Currency Payment
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Payment
                                    paySchedule={payments}
                                    payThisMuch={payThisMuch}
                                    setPayThisMuch={(amount) => setPayThisMuch(parseFloat(amount))}
                                    allowCustomPay={parseInt(booking['Type']) === 2}
                                    flywire
                                />

                                {payThisMuch > 0 && <div style={{ marginTop: 30 }}>
                                    <Typography align={'center'} id="transition-modal-title" variant={'h3'}>
                                        Pay Now ${payThisMuch.formatMoney()} USD
                                    </Typography>
                                    <CheckoutFormFlywire
                                        bookingId={id}
                                        payThisMuch={payThisMuch}
                                        billingDetails={billingDetails}
                                        sendFlywireToken={sendFlywireToken}
                                    />
                                </div>}
                            </AccordionDetails>
                        </Accordion>
                    }
                </Box>
            </Container>}
            {!allowPay && message && <Container sx={{ marginTop: '20px' }}>
                <Typography align={'center'} variant={'body1'}>
                    <Icon sx={{ fontSize: '140px', marginTop: '20px', marginBottom: '20px' }}>warning_amber</Icon><br />
                    {message}
                </Typography>
            </Container>
            }
    </>
  );
};

export default DashboardPaymentPage;